import { Grid, IconButton } from "@material-ui/core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import api from "../../config/AxiosConfig";
import CouponDialog from "../../pages/sale/coupon";
import { RxShoppingCartObject } from "../../pages/sale/SalePage";
import { setCart } from "../../redux/slices/cart-slice";
import { store } from "../../redux/store";
import {
	isNullOrUndefined,
	isNullOrUndefinedOrEmpty,
} from "../../utils/CommonFunctions";
import RxButton from "../RxButton";
import RxDialog from "../RxDialog";
import { RxDialogState } from "../RxDialog/RxDialog";
import RxPaper from "../RxPaper";
import RxShoppingCartItem from "../RxShoppingCartItem";
import RxTextField from "../RxTextField";
import RxTitleSeperator from "../RxTitleSeperator";
import RxTypography from "../RxTypography";
import styles from "./RxShoppingCart.module.css";

interface RxShoppingCartProps {
	className?: string;
}

const RxShoppingCart = (props: RxShoppingCartProps) => {
	const history = useHistory();

	const cartItem: RxShoppingCartObject = useSelector(
		(state: RootStateOrAny) => state.cart.cartItem
	);

	const setShoppingCart = (shoppingCart: RxShoppingCartObject | undefined) => {
		store.dispatch(
			setCart({
				cartItem: shoppingCart,
			})
		);
	};

	const [refreshShoppingCart, setRefreshShoppingCart] = useState(false);
	const [showCouponDialog, setShowCouponDialog] = useState(false);
	const [distributorCode, setDistributorCode] = useState(
		cartItem?.distributor?.code ?? ""
	);
	const [distributorDialogState, setDistributorDialogState] =
		useState<RxDialogState>({
			isOpen: false,
		} as RxDialogState);

	const getBasketInfo = async () => {
		await api.get("/1/basket").then((response: RxShoppingCartObject) => {
			setShoppingCart(response);
		});
	};

	useEffect(() => {
		getBasketInfo();
	}, []); // eslint-disable-line

	useEffect(() => {
		if (refreshShoppingCart) {
			getBasketInfo();
			setRefreshShoppingCart(false);
		}
	}, [refreshShoppingCart]); // eslint-disable-line

	useEffect(() => {
		if (isNullOrUndefined(cartItem?.distributor)) {
			setDistributorCode("");
		} else {
			setDistributorCode(cartItem?.distributor?.code ?? "");
		}
	}, [cartItem?.distributor]); // eslint-disable-line

	const couponDialogOnClose = () => {
		setShowCouponDialog(false);
	};

	const hasDiscount = (): boolean => {
		return (
			!isNullOrUndefined(cartItem) &&
			!isNullOrUndefined(cartItem.totalDiscount) &&
			cartItem?.totalDiscount?.toFixed(2) !== "0.00"
		);
	};

	const hasCartItem = (): boolean => {
		return !isNullOrUndefined(cartItem) &&
			!isNullOrUndefined(cartItem?.products) &&
			cartItem?.products
			? cartItem?.products?.length > 0
			: false;
	};

	const numberOfUserInCart = (): number => {
		const resultValue = cartItem?.products?.reduce(
			(sum, item) => sum + item.quantity,
			0
		);
		return resultValue ?? 0;
	};

	const checkDistributorCodesAndSendToPayment = () => {
		let oldDistributorCode = cartItem?.oldDistributor?.code ?? "";
		let newDistributorCode = cartItem?.distributor?.code ?? "";

		if (oldDistributorCode === "" && newDistributorCode === "") {
			history.push("/payment");
		} else {
			let description = "";
			if (oldDistributorCode === "" && newDistributorCode !== "") {
				description =
					"Yeni bir bayi kodu eklediniz. Bu bayi ile işleminize devam etmek istiyor musunuz?";
			} else if (oldDistributorCode !== "" && newDistributorCode === "") {
				description =
					"Önceki bayiniz: " +
					(cartItem?.oldDistributor?.name ?? "") +
					"\nBayi kodunu kaldırdınız. Bayi ile ilişkiniz kesilecektir. İşleme devam etmek istiyor musunuz?";
			} else if (oldDistributorCode !== newDistributorCode) {
				description =
					"Önceki bayiniz: " +
					(cartItem?.oldDistributor?.name ?? "") +
					"\nBayi kodunu değiştirdiniz. Yeni bayiniz " +
					(cartItem?.distributor?.name ?? "") +
					" olarak düzenlenecektir. İşleme devam etmek istiyor musunuz?";
			} else if (oldDistributorCode === newDistributorCode) {
				description =
					(cartItem?.oldDistributor?.name ?? "") +
					" bayisi ile işleme devam etmek istiyor musunuz?";
			}
			setDistributorDialogState({
				isOpen: true,
				title: "Bayi Bilgilendirme",
				message: description,
				status: "info",
			});
		}
	};

	const clearShoppingCart = async () => {
		await api
			.delete("/1/basket")
			.then(() => {
				setShoppingCart(undefined);
			})
			.catch(() => {
				setRefreshShoppingCart(true);
			});
	};

	const removeFromShoppingCart = async (productBasketId: number) => {
		await api
			.post("/1/basket/remove-item/" + productBasketId, {})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			})
			.catch(() => {
				setRefreshShoppingCart(true);
			});
	};

	const applyDistributor = async (code: string) => {
		await api
			.post("/1/basket/match-distributor/" + code, {})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			})
			.catch(() => {
				setRefreshShoppingCart(true);
			});
	};

	const applyCoupon = async (code: string) => {
		await api
			.post("/1/basket/add-coupon/" + code, {})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			})
			.catch(() => {
				setRefreshShoppingCart(true);
			});
	};

	const removeCoupon = async (code: string) => {
		await api
			.post("/1/basket/remove-coupon/" + code, {})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			})
			.catch(() => {
				setRefreshShoppingCart(true);
			});
	};

	const updateBasketProduct = async (
		productBasketId: number,
		quantity: number,
		licence: string | null
	) => {
		await api
			.post("/1/basket/update-product", {
				productBasketId: productBasketId,
				quantity: quantity,
				licence: licence,
			})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			});
	};

	return (
		<RxPaper
			className={styles.shoppingCartPaper + " " + props.className ?? ""}
		>
			<>
				<IconButton
					aria-label="delete"
					className={styles.iButtonRemove}
					disabled={!hasCartItem()}
					onClick={() => {
						clearShoppingCart();
					}}
				>
					<DeleteForeverIcon
						fontSize="inherit"
						color={!hasCartItem() ? "disabled" : "error"}
						className={styles.iconCircle}
					/>
				</IconButton>
				<Grid
					container
					spacing={1}
					direction="row"
					justifyContent="center"
					alignItems="center"
					alignContent="center"
				>
					<Grid item xs={12} sm={12}>
						<Grid container spacing={1}>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={12}>
									<RxTitleSeperator
										titleLabel={"SEPET"}
										className={styles.seperatorLineBottom}
									/>
								</Grid>
							</Grid>
							<Grid container spacing={1}>
								<div className={styles.cartItemContainer}>
									{hasCartItem()
										? cartItem?.products.map((item, index) => (
												<Grid
													item
													xs={12}
													sm={12}
													key={"grid-" + index}
												>
													<RxShoppingCartItem
														key={"rxsci" + index}
														item={item}
														licences={cartItem?.licences}
														refreshShoppingCart={
															setRefreshShoppingCart
														}
														removeFromShoppingCart={
															removeFromShoppingCart
														}
														updateBasketProduct={
															updateBasketProduct
														}
													/>
												</Grid>
										  ))
										: "Sepetinizde ürün bulunmamaktadır."}
								</div>
							</Grid>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={12}>
									<hr className={styles.topMargin} />
								</Grid>
							</Grid>
							<Grid
								container
								spacing={1}
								className={styles.topMarginFive}
							>
								<Grid item xs={6} sm={6}>
									<RxButton
										fullWidth
										color="primary"
										variant="contained"
										label="İNDİRİM KODLARIM"
										disabled={!hasCartItem()}
										onClick={() => {
											setShowCouponDialog(true);
										}}
									/>
								</Grid>
								<Grid item xs={6} sm={6}>
									{hasDiscount() && (
										<RxTypography
											variant={"body1"}
											align="right"
											label={
												"İndirim: " +
												(cartItem.totalDiscount * -1).toFixed(2) +
												" TL"
											}
											className={styles.discountStyleLabel}
										/>
									)}
								</Grid>
							</Grid>
							<Grid
								container
								spacing={1}
								className={styles.topMarginTen}
							>
								<Grid item xs={4} sm={4}>
									<RxTextField
										fullwidth
										variant="outlined"
										label="Bayi Kodu"
										placeholder="Bayi Kodu"
										size="small"
										value={distributorCode}
										disabled={
											!isNullOrUndefined(cartItem?.distributor) ||
											!hasCartItem()
										}
										onChange={(e) => {
											setDistributorCode(e.target.value);
										}}
									/>
								</Grid>
								<Grid item xs={2} sm={2}>
									<RxButton
										fullWidth
										color="primary"
										variant="contained"
										label={
											isNullOrUndefined(cartItem?.distributor)
												? "EKLE"
												: "SİL"
										}
										disabled={!hasCartItem()}
										onClick={() => {
											if (isNullOrUndefined(cartItem?.distributor)) {
												distributorCode &&
													applyDistributor(distributorCode);
											} else {
												applyDistributor("unset");
											}
										}}
										className={styles.minWidthUnset}
									/>
								</Grid>
								<Grid item xs={6} sm={6}>
									<RxTypography
										variant={"body1"}
										align="right"
										label={
											"KDV: " +
											(hasCartItem()
												? (
														cartItem?.totalPriceWithKdv -
														cartItem?.totalPrice
												  ).toFixed(2)
												: "0") +
											" TL"
										}
										className={styles.kdvStyleLabel}
									/>
									<RxTypography
										variant={"body1"}
										align="right"
										label={
											"Toplam: " +
											(hasCartItem()
												? cartItem?.totalPriceWithKdv.toFixed(2)
												: "0") +
											" TL"
										}
										className={styles.totalPriceStyleLabel}
									/>
								</Grid>
							</Grid>
							<Grid
								container
								spacing={0}
								className={styles.topMarginTen}
							>
								<Grid item xs={12} sm={12}>
									<RxTypography
										component="span"
										variant={"body1"}
										align="left"
										label={"Sepetinizde toplam"}
									/>
									<RxTypography
										component="span"
										variant={"body1"}
										align="left"
										label={" " + numberOfUserInCart() + " "}
										className={styles.boldFont}
									/>
									<RxTypography
										component="span"
										variant={"body1"}
										align="left"
										label={"kullanıcılık ürün bulunmaktadır."}
									/>
								</Grid>
							</Grid>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={12}>
									<RxButton
										fullWidth
										color="secondary"
										variant="contained"
										label="SEPETİ ONAYLA"
										disabled={!hasCartItem()}
										onClick={() => {
											checkDistributorCodesAndSendToPayment();
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				{showCouponDialog && (
					<CouponDialog
						title="İNDİRİM KODLARIM"
						onClose={couponDialogOnClose}
						applyCoupon={applyCoupon}
						removeCoupon={removeCoupon}
						coupons={cartItem?.products
							?.filter(
								(p) =>
									!isNullOrUndefinedOrEmpty(p.couponCode) &&
									p.showingCoupon
							)
							.map((item) => {
								return item.couponCode;
							})}
					/>
				)}
				{distributorDialogState.isOpen && (
					<RxDialog
						title={distributorDialogState.title}
						status={distributorDialogState.status}
						open={distributorDialogState.isOpen}
						firstButtonLabel={"İPTAL"}
						firstButtonAction={() => {
							setDistributorDialogState({
								isOpen: false,
							} as RxDialogState);
						}}
						firstButtonColor={"secondary"}
						secondButtonLabel={"DEVAM ET"}
						secondButtonColor={"primary"}
						secondButtonAction={() => {
							history.push("/payment");
						}}
					>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<RxTypography
									label={distributorDialogState.message}
									variant="subtitle1"
									align="justify"
									className={styles.typography}
								/>
							</Grid>
						</Grid>
					</RxDialog>
				)}
			</>
		</RxPaper>
	);
};

export default RxShoppingCart;
